import { motion } from 'framer-motion';
import { User, Users, BookOpen, Palette, Headphones, Sparkles } from 'lucide-react';
import { wizardSteps } from '../data/sampleStory.js';

// Icon mapping
const iconMap = {
  User: User,
  Users: Users,
  BookOpen: BookOpen,
  Palette: Palette,
  Headphones: Headphones,
  Sparkles: Sparkles,
};

// Step colors matching the old style
const stepColors = [
  { bg: 'bg-purple-100', iconBg: 'bg-white', iconText: 'text-purple-500' },
  { bg: 'bg-blue-100', iconBg: 'bg-white', iconText: 'text-blue-500' },
  { bg: 'bg-green-100', iconBg: 'bg-white', iconText: 'text-green-500' },
  { bg: 'bg-pink-100', iconBg: 'bg-white', iconText: 'text-pink-500' },
  { bg: 'bg-amber-100', iconBg: 'bg-white', iconText: 'text-amber-500' },
  { bg: 'bg-teal-100', iconBg: 'bg-white', iconText: 'text-teal-500' },
];

/**
 * WizardStepsSection - Vertical timeline with scroll animations (original style)
 */
export default function WizardStepsSection() {
  return (
    <section id="how-it-works" className="w-full py-8 bg-gradient-to-b from-purple-50 to-white border-2 border-purple-300 my-6">
      <div className="max-w-3xl px-4 mx-auto">
        <h2 className="section-header mb-8">How the Magic Happens</h2>
        <div className="relative">
          {/* Vertical timeline line */}
          <div className="absolute top-0 left-1/2 w-0.5 h-full bg-purple-200 transform -translate-x-1/2 rounded-full" />

          {wizardSteps.map((step, index) => {
            const IconComponent = iconMap[step.icon];
            const colors = stepColors[index % stepColors.length];

            return (
              <motion.div
                key={index}
                className={`relative z-10 flex items-center mb-3 ${index % 2 === 0 ? 'justify-start' : 'justify-end'}`}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 15 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: false, amount: 0.8 }}
              >
                <div className={`${colors.bg} p-3 rounded-lg shadow-md w-[45%]`}>
                  <div className="flex items-center mb-1">
                    <div className={`mr-2 p-1.5 rounded-full ${colors.iconBg}`}>
                      <IconComponent className={`h-5 w-5 ${colors.iconText}`} />
                    </div>
                    <h3 className="text-sm font-semibold">{step.title}</h3>
                  </div>
                  <p className="text-gray-700 text-xs leading-relaxed">{step.description}</p>
                </div>
                {/* Timeline dot */}
                <div
                  className={`absolute top-1/2 left-1/2 w-3 h-3 bg-purple-500 rounded-full transform -translate-y-1/2 -translate-x-1/2 hidden md:block`}
                />
              </motion.div>
            );
          })}
        </div>
        <div className="text-center mt-10">
          <h3 className="text-xl font-bold text-purple-700 mb-2">Your Magical Journey Awaits!</h3>
          <p className="text-sm text-gray-600">Create unforgettable stories that spark imagination and joy.</p>
        </div>
      </div>
    </section>
  );
}
